import React, { useCallback, useEffect, useLayoutEffect, useState, forwardRef, HTMLAttributes, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Icon } from '@skodaflow/web-library';
import { ContentDiv } from './contentDiv';
import {
  Button,
  FormControl,
  TextField,
  useMediaQuery,
  useTheme,
  Modal as MuiModal,
  ModalProps as MuiModalProps,
} from '@mui/material';
import { Html5Qrcode, Html5QrcodeResult } from 'html5-qrcode';
import { ModalTitle } from './modalTitle';

/**
 * Base definition of SkodaFlow Modal component independent on core implementation.
 */
export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * If true, the component is shown.
   */
  open: boolean;
  /**
   * Callback used to close the modal.
   */
  handleClose?: () => void;
  /**
   * Callback for the top button.
   */
  onClickTopButton?: () => void;
  /**
   * Callback for the bottom button.
   */
  onClickBottomButton?: () => void;
  /**
   * Accessibility.
   */
  ariaLabelledby?: string;
  /**
   * Accessibility.
   */
  ariaDescribedBy?: string;
  /**
   * Top button text.
   */
  firstButton?: string;
  /**
   * Bottom button text.
   */
  secondButton?: string;
  /**
   * If true, the second button is visible.
   */
  secondButtonVisible?: boolean;
  /**
   * If true, the buttons are displayed as compact. The feature is only working for mobile view.
   */
  hasCloseButton?: boolean;
  /**
   * The size of the modal in desktop view.
   */
  size?: 'small' | 'large' | 'full-width';
  /*
   * if false, the button section is hidden
   */
  defaultButtonSection?: boolean;
  /*
   * defines the max-width css property value
   */
  maxWidth?: string;
  /**
   * A callback used to track if the modal is fully loaded. When the modal is closed isLoaded is set to false.
   */
  modalLoadedCallback?: (isLoaded: boolean) => void;
  /**
   * A string displayed as label of textfield.
   */
  textfieldLabel?: string;
  /**
   *  A string displayed as hading of modal.
   */
  header?: string;
  /**
   *  Callback is used to track of what was scanned and automatically confirmed, or what was read or manually added and then manually confirmed.
   */
  onScannedResult?: (result: Html5QrcodeResult | string) => void;
  /**
   *  A callback is used to monitor that a modal has been closed without asserting a value.
   */
  onExit?: () => void;
}

/**
 * @description Styled SkodaFlow Modal component
 * @param props ModalProps
 * @returns React Functional Component
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      open,
      handleClose,
      onClickTopButton,
      onClickBottomButton,
      ariaLabelledby,
      ariaDescribedBy,
      firstButton,
      secondButton,
      hasCloseButton,
      size,
      textfieldLabel,
      header,
      secondButtonVisible,
      defaultButtonSection = true,
      maxWidth,
      modalLoadedCallback,
      ...props
    },
    ref,
  ) => {
    const [isOverFlowing, setIsOverFlowing] = useState<boolean>(false);
    const [desktopView, setDesktopView] = useState(false);
    const theme = useTheme();

    const [value, setValue] = useState('');
    const [started, setStarted] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    let html5QrCode: Html5Qrcode;

    useEffect(() => {
      if (open) {
        setValue('');
      }
    }, [open]);

    useLayoutEffect(() => {
      if (open && value === '') {
        startScanner();
      }
    }, [isLoaded]);

    modalLoadedCallback = (loaded: boolean) => {
      setIsLoaded(loaded);
    };

    const stopScanning = () => {
      try {
        if (html5QrCode) {
          html5QrCode
            .stop()
            .then(() => console.log('Stopped scanning'))
            .catch((err) => {
              console.error(err);
            });
        }
      } catch (error) {
        console.error(error);
      }
    };

    const onScanSuccess = useCallback((decodedText: string, decodedResult: Html5QrcodeResult) => {
      setValue(decodedText);
      props?.onScannedResult?.(decodedResult.decodedText);
      stopScanning();
      handleClose?.();
    }, []);

    const onScanFailure = () => {
      // handle scan failure, ignore and keep scanning.
    };

    const startScanner = useCallback(() => {
      if (document.getElementById('reader')) {
        html5QrCode = new Html5Qrcode('reader', { verbose: false });
        html5QrCode
          .start(
            { facingMode: 'environment' },
            {
              fps: 5, // Optional, frame per seconds for qr code scanning
              qrbox: { width: 250, height: 250 },
            },
            onScanSuccess,
            onScanFailure,
          )
          .catch((err) => {
            // Start failed, handle it.
            console.error('Cannot start scanning');
            console.error(err);
          });
      }
    }, []);

    const onScannerConfirm = useCallback(() => {
      setStarted(false);
      stopScanning();
      props?.onScannedResult?.(value);
      handleClose?.();
    }, []);

    const onInputChange = useCallback(
      (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
        setValue(event?.target?.value || '');
      },
      [],
    );

    if (!started) {
      setValue('');
      inputRef.current?.focus();
      setStarted(true);
    }

    const mq = useMediaQuery(`(min-width: ${theme.breakpoints.values.md}px)`);
    useEffect(() => {
      setDesktopView(mq);
    }, [theme.breakpoints.values.md]);

    const isCompactButton = useCallback(() => {
      const isModalLarge = (size || 'large') === 'large';
      if (desktopView && isModalLarge) return false;
    }, [desktopView, size]);

    return (
      <ModalStyled
        {...props}
        ref={ref}
        open={open}
        size={size}
        onClose={() => {
          stopScanning();
          handleClose?.();
        }}
        isOverFlowing={isOverFlowing}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedBy}
        compactButtons={isCompactButton()}
        maxWidth={maxWidth || ''}
        data-testid="Modal"
      >
        <div className="modal-container" data-testid="Modal.container">
          {hasCloseButton && (
            <button onClick={handleClose} data-testid="Modal.close">
              <Icon name="close" fontSize="medium" />
            </button>
          )}

          <ContentDiv
            setIsOverFlowing={setIsOverFlowing}
            className="modal-container--content"
            modalLoadedCallback={modalLoadedCallback}
            data-testid="Modal.content"
          >
            <ModalTitle>{header}</ModalTitle>
            <ReaderContainer>
              <div id="reader" style={{ width: '100%' }} />
            </ReaderContainer>

            <FormControl fullWidth>
              <TextField
                ref={inputRef}
                onChange={onInputChange}
                maxRows={14}
                value={value}
                label={textfieldLabel}
                autoFocus
              />
            </FormControl>
            <br />
            {children}
          </ContentDiv>
          {defaultButtonSection && (
            <div className="modal-container--action-buttons" data-testid="Modal.buttons">
              <Button
                color="primary"
                variant="contained"
                size="large"
                onClick={() => onScannerConfirm()}
                data-testid="Modal.button1"
              >
                {firstButton}
              </Button>
              {secondButtonVisible && (
                <Button
                  color="secondary"
                  variant="text"
                  size="large"
                  onClick={() => {
                    stopScanning();
                    handleClose?.();
                  }}
                  data-testid="Modal.button2"
                >
                  {secondButton}
                </Button>
              )}
            </div>
          )}
        </div>
      </ModalStyled>
    );
  },
);

/**
 * @description Modal styling definition
 */
const ModalStyled = styled(MuiModal, {
  shouldForwardProp: (prop) => prop !== 'compactButtons' && prop !== 'isOverFlowing' && prop !== 'maxWidth',
})<
  MuiModalProps & {
    isOverFlowing: boolean;
    compactButtons?: boolean;
    size?: 'small' | 'large' | 'full-width';
    maxWidth: string;
  }
>(({ theme, ...args }) => {
  const bottomButtonPadding = args.compactButtons ? '16px' : '24px';

  const smallModalWidth = '408px';
  const largeModalWidth = '624px';
  const fullWidthModal = args.maxWidth ? args.maxWidth : 'calc(100% - 40px)';

  const isModalLarge = (args.size || 'large') === 'large';
  const isModalFullWidth = args.size && args.size === 'full-width';
  const sizeBasedStyles = isModalLarge
    ? { maxWidth: largeModalWidth, marginTop: '40px', padding: '40px' }
    : isModalFullWidth
      ? { maxWidth: fullWidthModal, marginTop: '40px', padding: '40px' }
      : {
          maxWidth: smallModalWidth,
          marginTop: '48px',
          padding: '24px 40px 40px 40px',
        };

  return {
    '& .modal-container': {
      maxHeight: 'calc(100vh - 64px)',
      minHeight: '300px',
      display: 'flex',
      flexDirection: 'column',
      '&--content': {
        overflowX: 'hidden',
        overflowY: 'overlay',
        marginTop: '48px',
        padding: '0px 24px',
      },
      '&--action-buttons': {
        padding: '32px 24px',
        paddingBottom: bottomButtonPadding,
        boxShadow: args.isOverFlowing ? '0px 0px 32px rgba(0, 0, 0, 0.15)' : null,
        'div:first-of-type': {
          marginBottom: '15px',
        },
        button: {
          width: '100%',
        },
      },
    },
    [theme.breakpoints.up('md')]: {
      '& .modal-container': {
        maxHeight: 'calc(100vh - 160px)',
        margin: '0px',
        width: '100%',
        maxWidth: sizeBasedStyles.maxWidth,
        '&--content': {
          marginTop: sizeBasedStyles.marginTop,
          padding: '0px 40px',
        },
        '&--action-buttons': {
          padding: sizeBasedStyles.padding,
          paddingBottom: bottomButtonPadding,
        },
      },
    },
  };
});

export const ReaderContainer = styled('div')({
  justifyContent: 'center',
  maxHeight: '60%',
  display: 'flex',
  marginBottom: '16px',
  borderRadius: '14px',
  'video:first-of-type': {
    borderRadius: '15px',
  },
  'div:first-of-type': {
    borderRadius: '15px',
  },
});
